import { useEffect, useId, useMemo, useRef, useState } from "react";

import { nodeToLatex } from "@corca/latex";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NodeFieldLabel } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { InlineNodeComponent } from "@/components/expression/InlineNode";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { NumberInput } from "@/components/sidebar/NumberInput";
import {
  normalizeTaylorSeriesCenter,
  normalizeTaylorSeriesOrder,
  runTaylorSeriesAction,
  type TaylorSeriesSelection,
} from "@/input/utils/taylorSeriesAction";
import { DocumentModel } from "@/models/DocumentModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { twMerge } from "@/utils/twMerge";

import { TaylorSeriesVariableField } from "./TaylorSeriesVariableField";
import { useTaylorSeriesPreview } from "./useTaylorSeriesPreview";

type ReadySelection = Extract<TaylorSeriesSelection, { type: "ready" }>;

type Props = {
  doc: DocumentModel;
  selection: ReadySelection;
};

function createVariableInlineNode(doc: DocumentModel, name: string) {
  return nodeFromJson(
    doc,
    {
      type: "inline",
      props: {},
      children: [
        {
          type: "variable",
          props: {},
          children: [{ type: "symbol", props: { value: name }, children: [] }],
        },
      ],
    },
    { resetIds: true },
  );
}

export const TaylorSeriesPanel = observer(function TaylorSeriesPanel({ doc, selection }: Props) {
  const t = useTranslations("sidebar.taylorSeries");
  const centerLabelId = useId();
  const orderLabelId = useId();
  const [selectedVariable, setSelectedVariable] = useState("");
  const [center, setCenter] = useState(0);
  const [order, setOrder] = useState(6);
  const [isBusy, setIsBusy] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const previewDoc = useMemo(() => new DocumentModel("taylorSeriesPreview", null), []);

  useEffect(() => {
    return () => {
      previewDoc.dispose();
    };
  }, [previewDoc]);

  const { variableNames } = selection;
  const variableKey = variableNames.join("|");

  const variableName = variableNames.includes(selectedVariable)
    ? selectedVariable
    : (variableNames[0] ?? "");

  const variableMenuNodes = useMemo(() => {
    if (variableNames.length === 0) return {};
    return previewDoc.transact(() => {
      return Object.fromEntries(
        variableNames.flatMap((name) => {
          const node = createVariableInlineNode(previewDoc, name);
          return node?.is(InlineNodeModel) ? [[name, node] as const] : [];
        }),
      );
    }, "TaylorSeriesVariableMenuPreview");
  }, [previewDoc, variableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const variablePreviewNode: InlineNodeModel | null = variableMenuNodes[variableName] ?? null;

  const {
    preview: previewNodes,
    isLoading: isPreviewLoading,
    equals,
  } = useTaylorSeriesPreview({
    previewDoc,
    selection,
    variableName,
    center,
    order,
  });

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setCopyState("idle");
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
  }, [previewNodes]);

  // Hide the panel when the series output equals the input (no useful expansion)
  if (equals && !isPreviewLoading) return null;

  const normalizedCenter = normalizeTaylorSeriesCenter(center);
  const normalizedOrder = normalizeTaylorSeriesOrder(order);
  const isVariableValid = variableNames.includes(variableName);
  const canRun =
    normalizedCenter !== null && normalizedOrder !== null && isVariableValid && !isBusy;

  const canCopy = previewNodes.length > 0 && !isPreviewLoading;

  const handleCopy = async () => {
    if (!canCopy) return;
    const latex = previewNodes.map((node) => nodeToLatex(node.toJSON()).text).join("");
    if (!latex) return;
    try {
      await navigator.clipboard.writeText(latex);
      setCopyState("copied");
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopyState("idle"), 1500);
    } catch (e) {
      console.error("TaylorSeriesPanel: clipboard write failed", e);
    }
  };

  const handleApply = async () => {
    if (normalizedCenter === null || normalizedOrder === null || !isVariableValid) return;

    setIsBusy(true);
    await runTaylorSeriesAction(doc, selection, {
      variableName,
      center: normalizedCenter,
      order: normalizedOrder,
    });
    setIsBusy(false);
    doc.focus();
  };

  const renderPreview = () => {
    if (isPreviewLoading && previewNodes.length === 0) {
      return <span className="text-sm text-gray-500">{t("loading")}</span>;
    }

    if (previewNodes.length === 0) {
      return <span className="text-sm text-gray-500">{t("emptyPreview")}</span>;
    }

    return (
      <NodeStyleProvider renderContext="right-sidebar" size="sm">
        <InlineNodeComponent
          doc={previewDoc}
          nodes={previewNodes}
          className="select-text font-mono opacity-60"
        />
      </NodeStyleProvider>
    );
  };

  return (
    <ActionsDisclosure
      data-panel="taylor_series"
      title={t("title")}
      disclosureProps={{ defaultOpen: true }}
      className="w-full"
    >
      <div className="flex flex-col gap-2.5">
        <TaylorSeriesVariableField
          doc={previewDoc}
          name={variableName}
          node={variablePreviewNode}
          nodes={variableNames.map((n) => ({ name: n, node: variableMenuNodes[n] }))}
          isValid={isVariableValid}
          onSelect={setSelectedVariable}
        />
        <div className="flex gap-1">
          <div className="flex-1 flex flex-col gap-1">
            <span
              id={centerLabelId}
              className="font-mono text-xxxs uppercase tracking-tight text-gray-500 pl-1"
            >
              {t("center")}
            </span>
            <NumberInput
              value={center}
              onSubmit={setCenter}
              aria-labelledby={centerLabelId}
              aria-invalid={normalizedCenter === null}
              inputClassName={twMerge(normalizedCenter === null && "text-red-500")}
            />
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <span
              id={orderLabelId}
              className="font-mono text-xxxs uppercase tracking-tight text-gray-500 pl-1"
            >
              {t("order")}
            </span>
            <NumberInput
              value={order}
              min={1}
              onSubmit={setOrder}
              aria-labelledby={orderLabelId}
              aria-invalid={normalizedOrder === null}
              inputClassName={twMerge(normalizedOrder === null && "text-red-500")}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-mono text-xxxs uppercase tracking-tight text-gray-500 pl-1">
            {t("preview")}
          </span>
          <div className="relative">
            <NodeFieldLabel
              as="div"
              className="p-1 pr-7 min-h-12 flex-col items-start shadow-block dark:shadow-block-inverted overflow-x-auto no-scrollbar cursor-default"
            >
              {renderPreview()}
            </NodeFieldLabel>
            {canCopy && (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={t("copy")}
                title={copyState === "copied" ? t("copied") : t("copy")}
                className={twMerge(
                  "absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-4 bg-sidebar-bg/80 backdrop-blur-sm text-gray-500 hover:text-document-text hover:bg-sidebar-button-active-bg",
                  copyState === "copied" && "text-document-text",
                )}
              >
                <Icon name={copyState === "copied" ? "Check" : "Copy"} width={12} height={12} />
              </button>
            )}
          </div>
        </div>
        <FormButton
          variant="outline"
          disabled={!canRun}
          isBusy={isBusy}
          onClick={handleApply}
          className="flex items-center justify-center w-full h-7.5 border-gray-300 dark:border-gray-700 rounded-6"
        >
          <Icon name="Import" className="mr-1" />
          {t("insert")}
        </FormButton>
      </div>
    </ActionsDisclosure>
  );
});
